/**
 * The call view shared by the signed-in session page and the QR-joined room phone.
 *   - the instrument (Elara) is its own tile: video only, the main view
 *   - every other participant is a tile with video and audio
 *   - I publish my camera/mic once; my mic is live only when I carry audio (remote always; room only the device
 *     that owns the room audio), and a silent room device also plays no call audio, so one room never echoes
 */
import { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { Aperture, Microphone, MicrophoneSlash, Monitor, SpeakerSlash, UserCircle } from "@phosphor-icons/react";
import { ConnectionDots, NotForDiagnosis, Overlay, VideoFrame } from "./ui";
import { tones } from "./theme";
import { Connection, getLocalMedia, Turn, whepPlay, whipPublish } from "./webrtc";

export interface SessionState {
  id: string;
  status: string;
  startedAt: string;
  endedAt: string | null;
  patient: {
    label: string;
    full?: { name: string; mrn: string; birthDate: string | null; accession: string; procedure: string };
  };
  instrument: { nodeId: string; label: string; whepUrl: string; hasAudio: false };
  me: { participantId: string; kind: string; audio: boolean; whipUrl: string } | null;
  participants: {
    id: string;
    kind: string;
    device: string | null;
    label: string;
    audio: boolean;
    connected: boolean;
    whepUrl: string;
    isMe: boolean;
  }[];
  turn: Turn;
  canControl?: boolean;
}

/** Keeps a WHEP connection up, retrying while the far side is not publishing yet. */
function useRemoteStream(
  url: string,
  token: string,
  turn: Turn,
  opts: { audio: boolean; video: boolean },
  enabled: boolean
) {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [status, setStatus] = useState<"connecting" | "live" | "waiting">("connecting");
  const turnRef = useRef(turn);
  turnRef.current = turn;
  useEffect(() => {
    if (!enabled) return;
    let conn: Connection | null = null;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const connect = async () => {
      if (stopped) return;
      try {
        setStatus("connecting");
        conn = await whepPlay(url, token, turnRef.current, opts, (s) => setStream(s));
        conn.pc.onconnectionstatechange = () => {
          const st = conn?.pc.connectionState;
          if (st === "connected") setStatus("live");
          if (st === "failed" || st === "disconnected" || st === "closed") retry();
        };
      } catch {
        retry();
      }
    };
    const retry = () => {
      if (stopped) return;
      setStatus("waiting");
      conn?.close();
      conn = null;
      clearTimeout(timer);
      timer = setTimeout(connect, 4000);
    };
    connect();
    return () => {
      stopped = true;
      clearTimeout(timer);
      conn?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, token, enabled, opts.audio, opts.video]);
  return { stream, status };
}

function Tile({
  stream,
  muted,
  label,
  sub,
  big,
  badges,
  live,
  status,
}: {
  stream: MediaStream | null;
  muted: boolean;
  label: string;
  sub?: string;
  big?: boolean;
  badges?: React.ReactNode;
  live?: boolean;
  status?: React.ReactNode;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current && stream && ref.current.srcObject !== stream) ref.current.srcObject = stream;
  }, [stream]);
  useEffect(() => {
    if (ref.current) ref.current.muted = muted;
  }, [muted]);
  return (
    <VideoFrame
      aspect={big ? "16 / 9" : "4 / 3"}
      glow={live ? "live" : "warm"}
      sx={{ borderRadius: big ? "16px" : "12px" }}
    >
      <video
        ref={ref}
        autoPlay
        playsInline
        muted={muted}
        style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
      />
      {!stream && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "rgba(255,255,255,0.14)",
          }}
        >
          {big ? <Aperture size={72} weight="thin" /> : <UserCircle size={44} weight="thin" />}
        </Box>
      )}
      {status && <Box sx={{ position: "absolute", left: 10, top: 10, right: 10, display: "flex" }}>{status}</Box>}
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{ position: "absolute", left: 10, bottom: 10, right: 10, minWidth: 0 }}
      >
        <Overlay sx={{ fontWeight: 500 }}>{label}</Overlay>
        {sub && (
          <Overlay
            sx={{
              color: "text.secondary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
            }}
          >
            {sub}
          </Overlay>
        )}
        {badges}
      </Stack>
    </VideoFrame>
  );
}

function InstrumentTile({ state, token }: { state: SessionState; token: string }) {
  const { stream, status } = useRemoteStream(
    state.instrument.whepUrl,
    token,
    state.turn,
    { audio: false, video: true },
    state.status === "live"
  );
  return (
    <Tile
      big
      live={status === "live"}
      stream={stream}
      muted
      label={state.instrument.label}
      status={
        <Overlay>
          <ConnectionDots
            state={status === "live" ? "live" : status === "connecting" ? "connecting" : "waiting"}
            from={<Aperture size={15} />}
            to={<Monitor size={15} />}
            label={
              status === "live"
                ? "instrument live"
                : status === "connecting"
                ? "connecting to the instrument"
                : "waiting for the instrument stream"
            }
          />
        </Overlay>
      }
      badges={
        <Overlay sx={{ color: "text.secondary" }}>
          <SpeakerSlash size={13} />
          no audio
        </Overlay>
      }
    />
  );
}

function ParticipantTile({
  p,
  token,
  turn,
  playAudio,
  live,
}: {
  p: SessionState["participants"][number];
  token: string;
  turn: Turn;
  playAudio: boolean;
  live: boolean;
}) {
  const { stream, status } = useRemoteStream(p.whepUrl, token, turn, { audio: true, video: true }, live && p.connected);
  const role = p.kind === "room" ? (p.audio ? "room audio" : "room (muted)") : "remote";
  return (
    <Tile
      stream={stream}
      live={p.connected && status === "live"}
      muted={!playAudio || !p.audio}
      label={p.label}
      sub={!p.connected ? "disconnected" : status === "live" ? role : `${role} · connecting`}
    />
  );
}

/** My camera/mic, published once; the mic follows the `audio` flag from the session state. */
function useLocalPublish(state: SessionState, token: string, userMuted: boolean, cameraOff = false) {
  const [local, setLocal] = useState<MediaStream | null>(null);
  const [error, setError] = useState("");
  const whipUrl = state.me?.whipUrl;
  useEffect(() => {
    if (!whipUrl || state.status !== "live") return;
    let conn: Connection | null = null;
    let media: MediaStream | null = null;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Publish, and keep republishing if the relay refuses or the connection drops (relay restart, network change).
    const publish = async () => {
      if (stopped || !media) return;
      try {
        conn = await whipPublish(whipUrl, token, state.turn, media);
        setError("");
        conn.pc.onconnectionstatechange = () => {
          const st = conn?.pc.connectionState;
          if (st === "failed" || st === "disconnected") retry();
        };
      } catch (e) {
        setError(`Sending camera or microphone failed (${(e as Error).message}). Retrying.`);
        retry();
      }
    };
    const retry = () => {
      if (stopped) return;
      conn?.close();
      conn = null;
      clearTimeout(timer);
      timer = setTimeout(publish, 4000);
    };
    (async () => {
      try {
        media = await getLocalMedia();
        if (stopped) return media.getTracks().forEach((t) => t.stop());
        setLocal(media);
        await publish();
      } catch (e) {
        setError(`Camera or microphone error: ${(e as Error).message}`);
      }
    })();
    return () => {
      stopped = true;
      clearTimeout(timer);
      conn?.close();
      media?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whipUrl, token, state.status]);
  const micOn = !!state.me?.audio && !userMuted;
  useEffect(() => {
    local?.getAudioTracks().forEach((t) => (t.enabled = micOn));
  }, [local, micOn]);
  // When another device has the room (a phone scanned the code), this one goes dark as well as silent: the published
  // stream stays up so the handoff back is instant, but its video track sends nothing.
  useEffect(() => {
    local?.getVideoTracks().forEach((t) => (t.enabled = !cameraOff));
  }, [local, cameraOff]);
  return { local, error, micOn };
}

/**
 * Level of what this device is actually sending: the analyser reads the same track that is published, so a muted
 * (disabled) track reads flat. That is the proof the mic is off, not just a label.
 */
function useSendLevel(local: MediaStream | null) {
  const [level, setLevel] = useState(0);
  useEffect(() => {
    const track = local?.getAudioTracks()[0];
    if (!track) return;
    const ctx = new AudioContext();
    ctx.resume().catch(() => undefined); // iOS starts it suspended outside a tap
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    ctx.createMediaStreamSource(new MediaStream([track])).connect(analyser);
    const buf = new Uint8Array(analyser.fftSize);
    let raf = 0;
    const tick = () => {
      analyser.getByteTimeDomainData(buf);
      let peak = 0;
      for (const v of buf) peak = Math.max(peak, Math.abs(v - 128));
      setLevel(Math.min(100, (peak / 128) * 250));
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelAnimationFrame(raf);
      ctx.close().catch(() => undefined);
    };
  }, [local]);
  return level;
}

/** What this device is sending, as a row of bars that light up with the level. */
function LevelMeter({ value, on }: { value: number; on: boolean }) {
  const bars = 14;
  const lit = Math.round((value / 100) * bars);
  return (
    <Stack
      direction="row"
      spacing="3px"
      alignItems="center"
      sx={{ flexGrow: 1, height: 18 }}
      title="Level of audio sent from this device"
      role="meter"
      aria-valuenow={Math.round(value)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Microphone level"
    >
      {Array.from({ length: bars }, (_, i) => (
        <Box
          key={i}
          sx={{
            flex: 1,
            height: `${40 + (i / bars) * 60}%`,
            borderRadius: 2,
            bgcolor: on && i < lit ? (i > bars * 0.8 ? tones.warn : tones.live) : "rgba(255,255,255,0.08)",
            transition: "background-color 80ms linear",
          }}
        />
      ))}
    </Stack>
  );
}

export function SessionCall({
  state,
  token,
  onTakeRoomAudio,
}: {
  state: SessionState;
  token: string;
  onTakeRoomAudio?: () => void;
}) {
  const [userMuted, setUserMuted] = useState(false);
  const live = state.status === "live";
  // A room device that does not own the room audio stays silent both ways, and its camera is off too.
  const handedOff = state.me?.kind === "room" && !state.me.audio;
  const { local, error, micOn } = useLocalPublish(state, token, userMuted, handedOff);
  const level = useSendLevel(local);
  const playAudio = !handedOff;
  const owner = state.participants.find((p) => p.kind === "room" && p.audio);
  const others = state.participants.filter((p) => !p.isMe);
  return (
    <Stack spacing={1.5}>
      <NotForDiagnosis />
      {live && handedOff && (
        <Alert
          severity="info"
          icon={<MicrophoneSlash size={20} />}
          action={
            onTakeRoomAudio && (
              <Button color="inherit" size="small" onClick={onTakeRoomAudio}>
                Use this device for room audio
              </Button>
            )
          }
        >
          The room is on <b>{owner?.label ?? "another device"}</b>. This device's camera, mic and speaker are off.
        </Alert>
      )}
      <InstrumentTile state={state} token={token} />
      <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" } }}>
        {others.map((p) => (
          <ParticipantTile key={p.id} p={p} token={token} turn={state.turn} playAudio={playAudio} live={live} />
        ))}
        {state.me && (
          <Stack spacing={0.5}>
            <Tile
              stream={handedOff ? null : local}
              live={micOn}
              muted
              label="You"
              sub={
                micOn ? "camera and mic on" : handedOff ? "camera and mic off (the room is on another device)" : "muted"
              }
            />
            {live && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  variant={micOn ? "outlined" : "contained"}
                  color={micOn ? "secondary" : "warning"}
                  startIcon={micOn ? <Microphone size={16} /> : <MicrophoneSlash size={16} />}
                  disabled={handedOff}
                  onClick={() => setUserMuted((m) => !m)}
                  sx={{ flexShrink: 0 }}
                >
                  {handedOff ? "Mic off" : micOn ? "Mute" : "Unmute"}
                </Button>
                <LevelMeter value={micOn ? level : 0} on={micOn} />
              </Stack>
            )}
          </Stack>
        )}
      </Box>
      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}
    </Stack>
  );
}
